import React, { useEffect, useState } from "react";
import { Card, Select, Spin, Empty, Alert, Tag } from "antd";
import { ShopOutlined, SearchOutlined } from "@ant-design/icons";
import styles from "./EditProductBaseSupplierContainer.module.scss";
import TableFixedPage from "../../TableFixedPage/TableFixedPage";
import {
  EditSupplierProductAPI,
  getProductBaseSupplierId,
  getSupplierListApi,
} from "../../../Api/PanelAdminServices";
import { succToast, errorToast } from "../../../Utils/Toast/Toast";
import ProductBaseSupplierTable from "../../Tables/Product/ProductBaseSupplierTable/ProductBaseSupplierTable";

const EditProductBaseSupplierContainer = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [initialProducts, setInitialProducts] = useState({});
  const [modifiedProductIds, setModifiedProductIds] = useState({});
  const [savingProductIds, setSavingProductIds] = useState({});
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState("");

  useEffect(() => {
    setLoading(true);

    getSupplierListApi((success, response) => {
      setLoading(false);

      if (success && response?.status === 1) {
        setSuppliers(response.suppliers || []);
      } else {
        setError("دریافت لیست تأمین‌کنندگان با خطا مواجه شد.");
      }
    });
  }, []);

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplierId(supplierId);
    setProducts([]);
    setInitialProducts({});
    setModifiedProductIds({});
    setSavingProductIds({});
    setProductsError("");

    if (!supplierId) {
      setProductsLoading(false);
      return;
    }

    setProductsLoading(true);
    getProductBaseSupplierId(supplierId, (success, response) => {
      setProductsLoading(false);
      if (success && response?.status === 1) {
        const list = response.products || [];
        setProducts(list);
        const initMap = {};
        list.forEach((item) => {
          initMap[item.id] = { ...item };
        });
        setInitialProducts(initMap);
      } else {
        setProducts([]);
        setInitialProducts({});
        setProductsError("دریافت محصولات تأمین‌کننده با خطا مواجه شد.");
      }
    });
  };

  const selectedSupplier = suppliers.find(
    (supplier) => supplier.id === selectedSupplierId,
  );

  const supplierOptions = suppliers.map((supplier) => {
    const typeLabel =
      supplier.type === "food"
        ? "رستوران"
        : supplier.type === "supermarket"
          ? "سوپرمارکت"
          : "";
    const typeBadge = typeLabel ? ` [${typeLabel}]` : "";
    return {
      value: supplier.id,
      label: `${supplier.name || "بدون نام"}${typeBadge} - ${supplier.first_name || ""
        } ${supplier.last_name || ""}`.trim(),
      supplier,
    };
  });
  const updateProductField = (productId, field, value) => {
    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        if (product.id === productId) {
          const updated = { ...product, [field]: value };
          const original = initialProducts[productId];
          const isChanged =
            String(updated.count ?? "") !== String(original?.count ?? "") ||
            String(updated.price ?? "") !== String(original?.price ?? "") ||
            String(updated.off_price ?? "") !== String(original?.off_price ?? "");

          setModifiedProductIds((prev) => ({
            ...prev,
            [productId]: isChanged,
          }));
          return updated;
        }
        return product;
      }),
    );
  };

  const handleSaveProduct = (record) => {
    const productId = record.id;
    let countValue;
    if (selectedSupplier?.type === "food") {
      countValue = Number(record.count) > 0 ? 500 : 0;
    } else {
      countValue =
        record.count === "" || record.count == null ? 0 : Number(record.count);
    }

    const payload = {
      price: record.price ? String(record.price) : "0",
      off_price: record.off_price ? String(record.off_price) : "0",
      supplier_id: selectedSupplierId,
      product_id: productId,
      count: countValue,
    };

    setSavingProductIds((prev) => ({ ...prev, [productId]: true }));

    EditSupplierProductAPI(payload, (res) => {
      setSavingProductIds((prev) => ({ ...prev, [productId]: false }));

      if (res?.status === 1) {
        succToast(res?.message || "محصول با موفقیت ویرایش شد.");
        setInitialProducts((prev) => ({
          ...prev,
          [productId]: { ...record, count: countValue },
        }));
        setModifiedProductIds((prev) => ({
          ...prev,
          [productId]: false,
        }));
      } else {
        errorToast(res?.message || "خطا در ویرایش اطلاعات محصول.");
      }
    });
  };

  return (
    <div className={styles.container} dir="rtl">
      <TableFixedPage title={"ویرایش محصولات تامین کننده"} />
      <Card className={styles.card} bordered={false}>
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <ShopOutlined />
          </div>

          <div className={styles.headerText}>
            <p>ابتدا تأمین‌کننده موردنظر را انتخاب کنید.</p>
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            className={styles.error}
          />
        )}

        <div className={styles.selectorSection}>
          <label className={styles.label}>انتخاب تأمین‌کننده</label>

          <Select
            showSearch
            allowClear
            loading={loading}
            value={selectedSupplierId}
            placeholder="نام فروشگاه یا تأمین‌کننده را جست‌وجو کنید..."
            options={supplierOptions}
            optionFilterProp="label"
            onChange={(value, option) => {
              console.log("Select onChange fired:", value, option);
              handleSupplierChange(value);
            }}
            suffixIcon={<SearchOutlined />}
            className={styles.supplierSelect}
            size="large"
          />
        </div>

        {selectedSupplier && (
          <div className={styles.supplierDetails}>
            <div className={styles.supplierDetailsHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className={styles.supplierName}>
                  {selectedSupplier.name || "بدون نام"}
                </span>
                <Tag color={selectedSupplier.type === "food" ? "orange" : "blue"}>
                  {selectedSupplier.type === "food" ? "رستوران" : "سوپرمارکت"}
                </Tag>
              </div>

              <span className={styles.status}>
                {selectedSupplier.status === "1" ? "فعال" : "غیرفعال"}
              </span>
            </div>

            <div className={styles.supplierInfo}>
              <div className={styles.infoItem}>
                <span>نوع تأمین‌کننده</span>
                <strong>
                  {selectedSupplier.type === "food"
                    ? "رستوران"
                    : selectedSupplier.type === "supermarket"
                      ? "سوپرمارکت"
                      : "—"}
                </strong>
              </div>

              <div className={styles.infoItem}>
                <span>نام مسئول</span>
                <strong>
                  {`${selectedSupplier.first_name || ""} ${selectedSupplier.last_name || ""
                    }`.trim() || "—"}
                </strong>
              </div>

              <div className={styles.infoItem}>
                <span>شماره تماس</span>
                <strong>{selectedSupplier.phone || "ثبت نشده"}</strong>
              </div>

              <div className={styles.infoItem}>
                <span>تعداد محصولات</span>
                <strong>{selectedSupplier.total_product ?? "—"}</strong>
              </div>

              <div className={styles.infoItem}>
                <span>تعداد سفارش‌ها</span>
                <strong>{selectedSupplier.total_order ?? "—"}</strong>
              </div>
            </div>
          </div>
        )}

        {!loading && suppliers.length === 0 && !error && (
          <Empty
            description="تأمین‌کننده‌ای برای نمایش وجود ندارد."
            className={styles.empty}
          />
        )}
      </Card>

      {selectedSupplier && (
        <Card className={styles.productsCard} bordered={false}>
          <div className={styles.productsHeader}>
            <h3>محصولات تأمین‌کننده</h3>
            <span>
              {selectedSupplier.name || "بدون نام"}{" "}
              ({selectedSupplier.type === "food" ? "رستوران" : "سوپرمارکت"})
            </span>
          </div>

          <div className={styles.productsPlaceholder}>
            {productsLoading ? (
              <div className={styles.loadingWrapper}>
                <Spin size="large" />
                <span>در حال دریافت محصولات تأمین‌کننده...</span>
              </div>
            ) : productsError ? (
              <Alert
                message={productsError}
                type="error"
                showIcon
                className={styles.error}
              />
            ) : products.length > 0 ? (
              <ProductBaseSupplierTable
                products={products}
                onUpdateProduct={updateProductField}
                supplierType={selectedSupplier?.type}
                onSaveProduct={handleSaveProduct}
                savingProductIds={savingProductIds}
                modifiedProductIds={modifiedProductIds}
              />
            ) : (
              <Empty description="محصولی برای این تأمین‌کننده یافت نشد." />
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default EditProductBaseSupplierContainer;
